import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

const backendApi = config.backendApi;

/**
 * Turn an axios failure into an Error carrying the backend's human-readable
 * message. Loco serializes errors as `{ description }` or `{ error }`; raw
 * axios errors only expose "Request failed with status code 4xx", which hides
 * useful guidance like "your usage exceeds its limits — reduce usage first".
 */
function apiError(e: unknown, fallback: string): Error {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as { description?: string; error?: string } | undefined;
    const msg = data?.description ?? data?.error;
    if (msg) return new Error(msg);
  }
  return e instanceof Error ? e : new Error(fallback);
}

// Type definitions for billing API responses
export interface HourlyUsageSnapshot {
  snapshot_hour: string;
  resource_type: string;
  quantity: number;
  licensed_quantity: number;
  billable_quantity: number;
  billable_amount: string;
  is_overage: boolean;
  is_at_limit: boolean;
}

export interface ResourceSummary {
  resource_type: string;
  peak_usage: number;
  average_usage: number;
  licensed_quantity: number;
  total_overage_hours: number;
  total_billable_amount: string;
}

export interface MonthlyUsageSummary {
  month: string;
  billing_period_start: string;
  billing_period_end: string;
  total_hours_in_month: number;
  hours_processed: number;
  is_current_month: boolean;
  resource_summaries: ResourceSummary[];
  estimated_monthly_cost: string;
  current_monthly_cost: string;
}

interface UsageSnapshotsParams {
  start?: string; // ISO 8601 date string
  end?: string;   // ISO 8601 date string
}

// Fetch hourly usage snapshots
const fetchUsageSnapshots = async (params?: UsageSnapshotsParams): Promise<HourlyUsageSnapshot[]> => {
  const queryParams = new URLSearchParams();
  if (params?.start) queryParams.append('start', params.start);
  if (params?.end) queryParams.append('end', params.end);

  const url = `${backendApi}/api/billing/usage${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await axios.get<HourlyUsageSnapshot[]>(url, { withCredentials: true });
  return response.data;
};

// Fetch monthly usage summary
const fetchMonthlyUsageSummary = async (month?: string): Promise<MonthlyUsageSummary> => {
  const queryParams = new URLSearchParams();
  if (month) queryParams.append('month', month);

  const url = `${backendApi}/api/billing/monthly-summary${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await axios.get<MonthlyUsageSummary>(url, { withCredentials: true });
  return response.data;
};

// Hook for fetching hourly usage snapshots
export function useUsageSnapshots(params?: UsageSnapshotsParams, enabled = true) {
  const contextReady = useActiveContextReady();

  return useQuery<HourlyUsageSnapshot[]>({
    queryKey: ['billing', 'usage', params],
    queryFn: () => fetchUsageSnapshots(params),
    enabled: enabled && contextReady,
    refetchInterval: 60000, // Refetch every minute to get updates
  });
}

// Hook for fetching monthly usage summary
export function useMonthlyUsageSummary(month?: string, enabled = true) {
  const contextReady = useActiveContextReady();

  return useQuery<MonthlyUsageSummary>({
    queryKey: ['billing', 'monthly-summary', month],
    queryFn: () => fetchMonthlyUsageSummary(month),
    enabled: enabled && contextReady,
    refetchInterval: 60000, // Refetch every minute to get updates
  });
}

// Subscription management types
export interface BillingAccountDetails {
  organization_id: string;
  plan_id: number;
  plan_name: string;
  base_cost: string;
  billing_term: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  subscription_status: string | null;
  /** Subscription is scheduled to cancel at period end (resumable). */
  cancel_at_period_end: boolean;
  subscription_period_start: string | null;
  subscription_period_end: string | null;
  trial_end: string | null;
  next_billing_date: string | null;
  has_payment_method: boolean;
}

export interface ChangePlanRequest {
  new_plan_id: number;
  billing_term?: string;
  prorate?: boolean;
}

export interface CancelSubscriptionRequest {
  at_period_end?: boolean;
  /** Machine-readable option key from the cancel dialog (e.g. "too_expensive", "other"). */
  reason?: string;
  /** Free-text detail (the "Other" text or an optional elaboration). */
  comment?: string;
}

/** Billing address as Stripe's AddressElement returns it. */
export interface BillingAddress {
  line1: string;
  line2?: string;
  city: string;
  state?: string;
  postal_code: string;
  /** ISO 3166-1 alpha-2. */
  country: string;
}

export interface UpgradePlanRequest {
  plan_id: number;
  payment_method_id: string;
  billing_term?: string;
  trial_period_days?: number;
  /** Required: Stripe cannot compute tax without a customer location, and an
   * invoice raised without one fails rather than going out untaxed. */
  billing_address: BillingAddress;
}

export interface UpgradePlanResponse {
  success: boolean;
  stripe_customer_id: string;
  stripe_subscription_id: string;
  /// Stripe subscription status, lowercased (e.g., "active", "incomplete", "trialing").
  subscription_status: string;
  /// Set when the first invoice needs frontend PaymentIntent confirmation (3DS or
  /// general charge confirmation under default_incomplete behavior).
  client_secret?: string;
  message: string;
}

// Fetch current subscription details
const fetchSubscription = async (): Promise<BillingAccountDetails> => {
  const response = await axios.get<BillingAccountDetails>(
    `${backendApi}/api/billing/subscription`,
    { withCredentials: true }
  );
  return response.data;
};

// Change subscription plan
const changePlan = async (request: ChangePlanRequest): Promise<void> => {
  try {
    await axios.post(`${backendApi}/api/billing/change-plan`, request, {
      withCredentials: true,
    });
  } catch (e) {
    throw apiError(e, 'Failed to change plan.');
  }
};

// Cancel subscription
const cancelSubscription = async (request: CancelSubscriptionRequest): Promise<void> => {
  try {
    await axios.post(`${backendApi}/api/billing/cancel`, request, {
      withCredentials: true,
    });
  } catch (e) {
    throw apiError(e, 'Failed to cancel subscription.');
  }
};

// Resume a subscription scheduled to cancel at period end
const resumeSubscription = async (): Promise<void> => {
  try {
    await axios.post(`${backendApi}/api/billing/resume`, {}, { withCredentials: true });
  } catch (e) {
    throw apiError(e, 'Failed to resume subscription.');
  }
};

// Upgrade to paid plan
const upgradePlan = async (request: UpgradePlanRequest): Promise<UpgradePlanResponse> => {
  try {
    const response = await axios.post<UpgradePlanResponse>(
      `${backendApi}/api/billing/upgrade`,
      request,
      { withCredentials: true }
    );
    return response.data;
  } catch (e) {
    throw apiError(e, 'Failed to upgrade plan.');
  }
};

// Hook for fetching subscription details
export function useSubscription(enabled = true) {
  const contextReady = useActiveContextReady();

  return useQuery<BillingAccountDetails>({
    queryKey: ['billing', 'subscription'],
    queryFn: fetchSubscription,
    enabled: enabled && contextReady,
    staleTime: 30000, // 30 seconds
  });
}

// Hook for changing plan
export function useChangePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: changePlan,
    onSuccess: () => {
      // Invalidate and refetch subscription data
      void queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
      // A plan change can add/remove banners (e.g. the upgrade nag). Refetch so
      // an empty result clears the banner immediately, instead of lingering
      // until the 5-minute poll or a page reload.
      void queryClient.invalidateQueries({ queryKey: ['banners'] });
    },
  });
}

// Hook for canceling subscription
export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelSubscription,
    onSuccess: () => {
      // Invalidate and refetch subscription data
      void queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
      // A plan change can add/remove banners (e.g. the upgrade nag). Refetch so
      // an empty result clears the banner immediately, instead of lingering
      // until the 5-minute poll or a page reload.
      void queryClient.invalidateQueries({ queryKey: ['banners'] });
    },
  });
}

// Hook for resuming a subscription scheduled to cancel
export function useResumeSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: resumeSubscription,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
      void queryClient.invalidateQueries({ queryKey: ['banners'] });
    },
  });
}

// Hook for upgrading plan
export function useUpgradePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: upgradePlan,
    onSuccess: () => {
      // Invalidate and refetch subscription data
      void queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
      // A plan change can add/remove banners (e.g. the upgrade nag). Refetch so
      // an empty result clears the banner immediately, instead of lingering
      // until the 5-minute poll or a page reload.
      void queryClient.invalidateQueries({ queryKey: ['banners'] });
    },
  });
}

// Payment method management types
export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  is_default: boolean;
}

interface PaymentMethodsResponse {
  payment_methods: PaymentMethod[];
}

interface AddPaymentMethodRequest {
  payment_method_id: string;
  set_as_default?: boolean;
}

interface RetryPaymentResponse {
  success: boolean;
  invoice_id: string;
  invoice_status: string;
  message: string;
}

const PAYMENT_METHODS_KEY = ['billing', 'payment-methods'];

const fetchPaymentMethods = async (): Promise<PaymentMethod[]> => {
  const response = await axios.get<PaymentMethodsResponse>(
    `${backendApi}/api/billing/payment-methods`,
    { withCredentials: true }
  );
  return response.data.payment_methods;
};

const addPaymentMethod = async (request: AddPaymentMethodRequest): Promise<void> => {
  await axios.post(`${backendApi}/api/billing/payment-method`, request, {
    withCredentials: true,
  });
};

const setDefaultPaymentMethod = async (paymentMethodId: string): Promise<void> => {
  await axios.post(
    `${backendApi}/api/billing/payment-methods/${encodeURIComponent(paymentMethodId)}/default`,
    {},
    { withCredentials: true }
  );
};

const removePaymentMethod = async (paymentMethodId: string): Promise<void> => {
  await axios.delete(
    `${backendApi}/api/billing/payment-methods/${encodeURIComponent(paymentMethodId)}`,
    { withCredentials: true }
  );
};

const retryPayment = async (): Promise<RetryPaymentResponse> => {
  const response = await axios.post<RetryPaymentResponse>(
    `${backendApi}/api/billing/retry-payment`,
    {},
    { withCredentials: true }
  );
  return response.data;
};

// Hook for listing payment methods
export function usePaymentMethods(enabled = true) {
  const contextReady = useActiveContextReady();

  return useQuery<PaymentMethod[]>({
    queryKey: PAYMENT_METHODS_KEY,
    queryFn: fetchPaymentMethods,
    enabled: enabled && contextReady,
    staleTime: 30000,
  });
}

// Hook for adding a payment method
export function useAddPaymentMethod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addPaymentMethod,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PAYMENT_METHODS_KEY });
      void queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
    },
  });
}

// Hook for setting an existing payment method as default
export function useSetDefaultPaymentMethod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: setDefaultPaymentMethod,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PAYMENT_METHODS_KEY });
      void queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
    },
  });
}

// Hook for detaching a payment method
export function useRemovePaymentMethod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removePaymentMethod,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PAYMENT_METHODS_KEY });
      void queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
    },
  });
}

// Hook for retrying a failed/open invoice payment
export function useRetryPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: retryPayment,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
      void queryClient.invalidateQueries({ queryKey: ['banners'] });
    },
  });
}
